import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useQuote } from '../context/QuoteContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeft, Zap, AlertCircle, CheckCircle2, Package, ListChecks, FileText, Loader2,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { productImage } from '../utils/imageUtils';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Parses pasted text into [{article, quantity}]
// Accepts:
//   - One per line: "4244390" or "4244391×3" or "4244392 x 3" or "4244393 *3"
//   - Comma/semicolon separated: "4244390, 4244391x2; 4244392"
//   - With qty separators: x, ×, *, ;
export const parseArticleInput = (raw) => {
  if (!raw) return [];
  const tokens = raw
    .split(/[\n,;]/)
    .map((t) => t.trim())
    .filter(Boolean);

  return tokens
    .map((token) => {
      // Match "article x N" or "article × N" or "article * N" or "article qty N"
      const match = token.match(/^([A-Za-z0-9\-_./]+?)\s*[x×*\s]\s*(\d+)$/i);
      if (match) {
        return { article: match[1].trim(), quantity: parseInt(match[2], 10) || 1 };
      }
      return { article: token, quantity: 1 };
    })
    .filter((e) => e.article);
};

const QuickQuotePage = () => {
  const { language } = useLanguage();
  const { addToQuote } = useQuote();
  const navigate = useNavigate();

  const [rawText, setRawText] = useState('');
  const [matched, setMatched] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const t = (sv, en) => (language === 'sv' ? sv : en);

  const handleMatch = async () => {
    const articles = parseArticleInput(rawText);
    if (articles.length === 0) {
      toast.error(t('Klistra in artikelnummer först', 'Paste article numbers first'));
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/products/match-articles`, { articles });
      setMatched(res.data.matched || []);
      setUnmatched(res.data.unmatched || []);
      // Pre-select all matched by default
      setSelected(new Set((res.data.matched || []).map((_, idx) => idx)));
      setHasSearched(true);
      const m = res.data.match_count || 0;
      const u = res.data.unmatch_count || 0;
      if (m > 0) {
        toast.success(t(`${m} produkter hittade${u ? `, ${u} okända` : ''}`, `${m} products found${u ? `, ${u} unknown` : ''}`));
      } else {
        toast.error(t('Inga produkter hittades', 'No products found'));
      }
    } catch (err) {
      toast.error(t('Något gick fel', 'Something went wrong'));
    } finally {
      setLoading(false);
    }
  };

  const toggleSelected = (idx) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === matched.length) setSelected(new Set());
    else setSelected(new Set(matched.map((_, idx) => idx)));
  };

  const updateQty = (idx, delta) => {
    setMatched((prev) => prev.map((m, i) => {
      if (i !== idx) return m;
      const next = Math.max(1, Math.min(9999, (m.quantity || 1) + delta));
      return { ...m, quantity: next };
    }));
  };

  const handleAddSelected = () => {
    if (selected.size === 0) {
      toast.error(t('Välj minst en produkt', 'Select at least one product'));
      return;
    }
    let added = 0;
    matched.forEach((m, idx) => {
      if (!selected.has(idx)) return;
      const product = {
        id: m.productId,
        name_sv: m.productName_sv,
        name_en: m.productName_en,
        image_url: m.productImage,
      };
      addToQuote(product, m.variant, m.quantity || 1);
      added += 1;
    });
    toast.success(t(`${added} produkter tillagda i offertlistan`, `${added} products added to quote`));
    navigate('/offert');
  };

  const exampleText = '4244390\n4244391 x 3\n5758986; 2\nF626BA-50';

  return (
    <div className="bg-[#FAFAF8] min-h-screen pt-24" data-testid="quick-quote-page">
      <SEO
        title={t('Snabboffert | Klistra in artikelnummer', 'Quick Quote | Paste article numbers')}
        description={t(
          'Klistra in en lista med artikelnummer och få direkt en färdig offertlista. Spara tid när du gör återkommande beställningar.',
          'Paste a list of article numbers and get an instant quote list. Save time on repeat orders.',
        )}
        language={language}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/offert"
            className="text-stone-500 hover:text-stone-700 text-sm flex items-center gap-1 mb-3"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('Tillbaka till offertlista', 'Back to quote list')}
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#321a27] text-white flex items-center justify-center">
              <Zap className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-stone-900">
                {t('Snabboffert', 'Quick Quote')}
              </h1>
              <p className="text-stone-600 mt-1">
                {t(
                  'Klistra in artikelnummer från ditt kalkylprogram och få en färdig offertlista på sekunder.',
                  'Paste article numbers from your calculation tool and get an instant quote list.',
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input */}
          <div className="bg-white border border-stone-200">
            <div className="px-5 py-4 border-b border-stone-200 bg-stone-50">
              <h2 className="font-semibold text-stone-900 flex items-center gap-2">
                <ListChecks className="w-5 h-5" />
                {t('1. Klistra in artikelnummer', '1. Paste article numbers')}
              </h2>
            </div>
            <div className="p-5">
              <Textarea
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder={`${t('Ett per rad eller separerade med komma', 'One per line or comma-separated')}\n\n${t('Exempel', 'Example')}:\n${exampleText}`}
                rows={12}
                data-testid="quick-quote-input"
                className="bg-white font-mono text-sm rounded-none border-stone-300 focus:border-[#321a27]"
              />
              <div className="mt-3 text-xs text-stone-500 space-y-1">
                <p className="font-medium text-stone-700">
                  {t('Stödda format:', 'Supported formats:')}
                </p>
                <ul className="list-disc list-inside space-y-0.5 ml-1">
                  <li><code className="bg-stone-100 px-1">4244390</code> – {t('endast nummer (antal: 1)', 'just the number (qty: 1)')}</li>
                  <li><code className="bg-stone-100 px-1">4244391 x 3</code> – {t('med antal (x, ×, eller *)', 'with quantity (x, ×, or *)')}</li>
                  <li><code className="bg-stone-100 px-1">4244392; 2</code> – {t('separerat med semikolon', 'separated by semicolon')}</li>
                </ul>
              </div>
              <Button
                onClick={handleMatch}
                disabled={loading || !rawText.trim()}
                data-testid="quick-quote-match-btn"
                className="mt-4 w-full bg-[#321a27] hover:bg-[#4a2d3d] text-white rounded-none"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('Söker...', 'Matching...')}</>
                ) : (
                  <><Zap className="w-4 h-4 mr-2" /> {t('Matcha mot katalogen', 'Match against catalog')}</>
                )}
              </Button>
            </div>
          </div>

          {/* Results */}
          <div className="bg-white border border-stone-200">
            <div className="px-5 py-4 border-b border-stone-200 bg-stone-50 flex items-center justify-between">
              <h2 className="font-semibold text-stone-900 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                {t('2. Resultat', '2. Results')}
              </h2>
              {matched.length > 0 && (
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-xs text-[#321a27] hover:underline"
                  data-testid="quick-quote-toggle-all"
                >
                  {selected.size === matched.length
                    ? t('Avmarkera alla', 'Deselect all')
                    : t('Markera alla', 'Select all')}
                </button>
              )}
            </div>

            {!hasSearched ? (
              <div className="p-12 text-center text-stone-400">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="text-sm">
                  {t('Resultaten visas här efter du matchat.', 'Results will appear here after matching.')}
                </p>
              </div>
            ) : (
              <div>
                {/* Matched */}
                {matched.length > 0 && (
                  <div className="divide-y divide-stone-100 max-h-[420px] overflow-y-auto">
                    {matched.map((m, idx) => {
                      const isSelected = selected.has(idx);
                      return (
                        <div
                          key={`${m.productId}-${m.article}-${idx}`}
                          className={`p-3 flex items-center gap-3 transition-colors ${
                            isSelected ? 'bg-emerald-50/40' : 'bg-white'
                          }`}
                          data-testid={`matched-row-${idx}`}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelected(idx)}
                            data-testid={`matched-check-${idx}`}
                          />
                          {m.productImage ? (
                            <img
                              src={productImage(m.productImage)}
                              alt=""
                              className="w-12 h-12 object-contain bg-white border border-stone-200 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-stone-100 border border-stone-200 flex items-center justify-center flex-shrink-0">
                              <Package className="w-5 h-5 text-stone-300" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                              <span className="text-xs font-mono bg-emerald-100 text-emerald-800 px-1.5 py-0.5">
                                {m.article}
                              </span>
                              <Link
                                to={`/produkter/${m.productSlug || m.productId}`}
                                className="text-sm font-medium text-stone-900 hover:text-[#321a27] truncate"
                              >
                                {language === 'sv' ? m.productName_sv : m.productName_en}
                              </Link>
                            </div>
                            <p className="text-xs text-stone-500 truncate mt-0.5">
                              {Object.entries(m.variant || {})
                                .filter(([k, v]) => v && !['Artikelnummer', 'artikelnummer'].includes(k))
                                .slice(0, 3)
                                .map(([k, v]) => `${k}: ${v}`)
                                .join('  ·  ')}
                            </p>
                          </div>
                          <div className="flex items-center border border-stone-300 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => updateQty(idx, -1)}
                              className="w-7 h-7 hover:bg-stone-100 text-stone-700"
                              data-testid={`matched-qty-minus-${idx}`}
                            >−</button>
                            <span className="w-9 text-center text-sm font-medium" data-testid={`matched-qty-${idx}`}>
                              {m.quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() => updateQty(idx, 1)}
                              className="w-7 h-7 hover:bg-stone-100 text-stone-700"
                              data-testid={`matched-qty-plus-${idx}`}
                            >+</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Unmatched */}
                {unmatched.length > 0 && (
                  <div className="border-t-4 border-amber-100 bg-amber-50/40">
                    <div className="px-4 py-2 text-xs font-medium text-amber-800 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {t(`${unmatched.length} okända artikelnummer`, `${unmatched.length} unknown article numbers`)}
                    </div>
                    <ul className="px-4 pb-3 space-y-1">
                      {unmatched.map((u, idx) => (
                        <li key={idx} className="text-xs font-mono text-stone-700 flex items-center gap-2">
                          <span className="bg-stone-200 px-1.5 py-0.5">{u.article}</span>
                          {u.quantity > 1 && <span className="text-stone-500">× {u.quantity}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {matched.length === 0 && unmatched.length > 0 && (
                  <div className="p-6 text-center text-sm text-stone-500">
                    {t('Inga matchande artikelnummer hittades. Kontrollera stavning eller kontakta oss.', 'No matching article numbers found. Check spelling or contact us.')}
                  </div>
                )}

                {/* Action button */}
                {matched.length > 0 && (
                  <div className="p-4 border-t border-stone-200">
                    <Button
                      onClick={handleAddSelected}
                      disabled={selected.size === 0}
                      data-testid="quick-quote-add-btn"
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-none"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      {t(
                        `Lägg till ${selected.size} produkter i offertlista →`,
                        `Add ${selected.size} products to quote →`,
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickQuotePage;
